// Shared helpers for validating that a resolved location (from any source — manual
// address search, browser geolocation, IP-based geolocation, or plan-central-zip fallback)
// actually falls within the current plan's expected state, rather than trusting whatever
// coordinates/address a given detection method happened to produce.
//
// Originally this logic (resolveStateCode/validateLocationState) lived ONLY inside
// locationPanel.jsx and was only ever invoked from that component's manual address-search
// flow. That left every OTHER location-resolution path (search.jsx's and results.jsx's
// browser-geolocation, IP-geolocation, and central-zip fallbacks) completely unchecked: any
// resolved address was accepted and tagged `planCode: resolvedEnvPlan` unconditionally,
// even if its actual state didn't match the plan at all (e.g. a CA address stamped
// `planCode: "PA"` because the user happened to be on the PA plan's URL when browser/IP
// geolocation resolved to California — a real-world case that motivated pulling this out
// into a shared, reusable module instead of leaving it locationPanel.jsx-only).

// Local 2-letter-code -> full-name crosswalk. None of the plan-features/facets APIs this
// app talks to reliably expose full US state names anywhere, so this is a small,
// self-contained local table rather than trying to source it from an API response.
export const STATE_ABBREVIATION_TO_NAME = {
    AL: 'Alabama', AK: 'Alaska', AZ: 'Arizona', AR: 'Arkansas', CA: 'California',
    CO: 'Colorado', CT: 'Connecticut', DE: 'Delaware', FL: 'Florida', GA: 'Georgia',
    HI: 'Hawaii', ID: 'Idaho', IL: 'Illinois', IN: 'Indiana', IA: 'Iowa',
    KS: 'Kansas', KY: 'Kentucky', LA: 'Louisiana', ME: 'Maine', MD: 'Maryland',
    MA: 'Massachusetts', MI: 'Michigan', MN: 'Minnesota', MS: 'Mississippi', MO: 'Missouri',
    MT: 'Montana', NE: 'Nebraska', NV: 'Nevada', NH: 'New Hampshire', NJ: 'New Jersey',
    NM: 'New Mexico', NY: 'New York', NC: 'North Carolina', ND: 'North Dakota', OH: 'Ohio',
    OK: 'Oklahoma', OR: 'Oregon', PA: 'Pennsylvania', RI: 'Rhode Island', SC: 'South Carolina',
    SD: 'South Dakota', TN: 'Tennessee', TX: 'Texas', UT: 'Utah', VT: 'Vermont',
    VA: 'Virginia', WA: 'Washington', WV: 'West Virginia', WI: 'Wisconsin', WY: 'Wyoming',
    DC: 'District of Columbia'
};

/**
 * Resolves the definitive 2-letter state/env code for the current plan.
 *
 * Prefers `planCode` from PlanFeaturesContext — the SAME value sourced from the URL's
 * `:planCode` path segment (e.g. "FL", "AZ") and already flowing correctly into context via
 * `setPlanCode()`. Falls back to `planFeatures?.state`/`stateCode` only for
 * forward-compatibility, in case some environment's API response does carry one of those
 * fields (see locationPanel.jsx's original comment on why planCode is the reliable source).
 *
 * @param {string|null|undefined} planCode
 * @param {object|null|undefined} planFeatures
 * @returns {string|null} 2-letter uppercase state code, or null if it can't be determined.
 */
export function resolveExpectedStateCode(planCode, planFeatures) {
    if (planCode && planCode.length === 2) {
        return planCode.toUpperCase();
    }

    // Compound plan codes like "MI-DSNP", "OH-RISE", "IL-DSNP" — extract the 2-letter prefix.
    if (planCode?.includes('-')) {
        const prefix = planCode.split('-')[0];
        if (prefix.length === 2) return prefix.toUpperCase();
    }

    let stateFromPlan = planFeatures?.state || planFeatures?.stateCode || null;
    // Parse state code if it contains a hyphen (e.g., "AZ-DNSP" -> "AZ")
    if (stateFromPlan && stateFromPlan.includes('-')) {
        stateFromPlan = stateFromPlan.split('-')[0];
    }
    return (stateFromPlan && stateFromPlan.length === 2) ? stateFromPlan.toUpperCase() : null;
}

/**
 * Checks whether a resolved location's stateCode matches the plan's expected state.
 *
 * Fails OPEN (returns true) when either side of the comparison can't be determined — a
 * genuinely unknown expected state, or a location with no stateCode AND no coordinates
 * usable for the bounding-box fallback below — rather than blocking every search. This
 * matches the fail-open posture locationPanel.jsx's fetchSuggestions already takes when no
 * state code is available for the autocomplete restriction.
 *
 * Reported bug: testers were "still seeing a lot of results that aren't the correct state"
 * after using the Edit Location search. Root cause was this function failing open whenever
 * `locationData.stateCode` was missing/empty — which happens more than the "genuinely
 * unknown" case above suggests: Google's Place `administrative_area_level_1` component
 * (the field this whole app sources stateCode from) is occasionally absent from a place's
 * addressComponents entirely (seen for some POI-type places, and for a handful of
 * international-adjacent border addresses), silently letting a wrong-state address through
 * with no error shown to the user at all. Now, whenever stateCode is missing/empty but
 * lat/lng coordinates ARE available, falls back to a bounding-box containment check
 * (isCoordInStateBounds) instead of giving up — only truly fails open when NEITHER a
 * stateCode NOR usable coordinates are available to check against.
 *
 * @param {{stateCode?: string, latitude?: number, longitude?: number}|null|undefined} locationData
 * @param {string|null} expectedStateCode - result of resolveExpectedStateCode()
 * @returns {boolean} true if the location is acceptable (matches, or can't be determined)
 */
export function isLocationInExpectedState(locationData, expectedStateCode) {
    if (!expectedStateCode) return true;

    if (locationData?.stateCode) {
        return locationData.stateCode.toUpperCase() === expectedStateCode;
    }

    // No stateCode text to compare — fall back to a geographic bounding-box check against
    // the coordinates, if we have them, rather than immediately failing open.
    if (typeof locationData?.latitude === 'number' && typeof locationData?.longitude === 'number') {
        const inBounds = isCoordInStateBounds(locationData.latitude, locationData.longitude, expectedStateCode);
        // isCoordInStateBounds itself returns true (fails open) if the expected state has
        // no known bounding box, or the coordinates are missing/invalid — see its own doc.
        return inBounds;
    }

    // Neither a stateCode nor coordinates to check — genuinely can't determine, fail open.
    return true;
}

/**
 * Attempts to pull a 2-letter state code out of a Google Places prediction's
 * `secondary_text` (e.g. "Pittsburgh, PA, USA" or "Pittsburgh, Pennsylvania, USA").
 * Only recognizes the common, unambiguous formats — anything else (ZIP-only predictions
 * whose secondary_text is just "USA", unusual POI formats, missing state info entirely)
 * returns null rather than guessing, so callers can fail open on anything ambiguous.
 *
 * @param {string|null|undefined} secondaryText
 * @returns {string|null} 2-letter uppercase state code, or null if not confidently found.
 */
export function extractStateCodeFromSecondaryText(secondaryText) {
    if (!secondaryText) return null;

    // "City, ST" or "City, ST, USA" — a standalone 2-letter uppercase token immediately
    // preceded by ", " (or the start of the string) and followed by "," or the end.
    const abbrMatch = secondaryText.match(/(?:^|,\s*)([A-Z]{2})(?=\s*(?:,|$))/);
    if (abbrMatch) return abbrMatch[1];

    // "City, Full State Name, USA" — check for any known full state name as a whole word.
    for (const [code, name] of Object.entries(STATE_ABBREVIATION_TO_NAME)) {
        if (new RegExp(`\\b${name}\\b`, 'i').test(secondaryText)) return code;
    }

    return null;
}

/**
 * Whether a Places autocomplete suggestion's secondary_text unambiguously names a DIFFERENT
 * state than the plan's expected one — i.e. a real "out of state suggestion still showing
 * up" case, not just an ambiguous/unrecognized format.
 *
 * This is intentionally narrower than a general filter: it only excludes a suggestion when
 * extractStateCodeFromSecondaryText finds a clear, different state code. Anything ambiguous
 * (no state text at all, an unrecognized format, ZIP-only predictions whose secondary_text
 * is just "USA") is left alone/shown — see locationPanel.jsx's fetchSuggestions for why an
 * earlier, broader text-filtering attempt here was removed entirely (it dropped valid ZIP
 * and address suggestions). The hard `locationRestriction`/`bounds` rectangle sent to
 * Google (see STATE_BOUNDING_BOXES below) is deliberately generous and always over-includes
 * some neighboring-state territory near the border, so this check exists specifically to
 * catch the suggestions that fall inside that rectangle but are still, per Google's own
 * formatted text, actually in a different state.
 *
 * @param {string|null|undefined} secondaryText
 * @param {string|null|undefined} expectedStateCode
 * @returns {boolean}
 */
export function isSuggestionOutOfState(secondaryText, expectedStateCode) {
    if (!expectedStateCode) return false;
    const foundCode = extractStateCodeFromSecondaryText(secondaryText);
    if (!foundCode) return false;
    return foundCode.toUpperCase() !== expectedStateCode.toUpperCase();
}

// Approximate rectangular (min/max lat/lng) bounding boxes for every US state + DC, used as
// a fallback geographic check (isCoordInStateBounds) when a resolved location has no
// stateCode text to compare directly, and as a hard `locationRestriction`/`bounds` value for
// Google Places Autocomplete requests (see locationPanel.jsx) so suggestions are actually
// constrained to the plan's state instead of merely SOFT-biased toward a central point with
// a small (50km max) radius — the previous approach, which left the vast majority of most
// states' area completely outside the bias radius, and applied no hard restriction at all.
// These are deliberately generous rectangular envelopes (not precise state borders) — a
// bounding box always over-includes some area from neighboring states near the border,
// which is fine for a "soft sanity check"/coarse restriction; it should never under-include
// real addresses within the state itself. Coordinates: [south, west, north, east].
export const STATE_BOUNDING_BOXES = {
    AL: [30.1,  -88.5, 35.1,  -84.8], AK: [51.0, -179.5, 71.6, -129.0],
    AZ: [31.2,  -114.9, 37.1,  -108.9], AR: [32.9, -94.7, 36.6, -89.5],
    CA: [32.4,  -124.5, 42.1,  -114.0], CO: [36.9, -109.2, 41.1, -101.9],
    CT: [40.9,  -73.8,  42.1,  -71.7],  DE: [38.4, -75.9,  39.9,  -74.9],
    DC: [38.79, -77.13, 39.0,  -76.9],
    FL: [24.4,  -87.7,  31.1,  -79.9],  GA: [30.3, -85.7,  35.1,  -80.7],
    HI: [18.8,  -160.3, 22.3,  -154.7], ID: [41.9, -117.3, 49.1, -110.9],
    IL: [36.9,  -91.6,  42.6,  -87.0],  IN: [37.7, -88.1,  41.8,  -84.7],
    IA: [40.3,  -96.7,  43.6,  -90.0],  KS: [36.9, -102.1, 40.1,  -94.5],
    KY: [36.4,  -89.6,  39.2,  -81.9],  LA: [28.8, -94.1,  33.1,  -88.7],
    ME: [42.9,  -71.2,  47.5,  -66.8],  MD: [37.8, -79.5,  39.8,  -74.9],
    MA: [41.2,  -73.6,  42.9,  -69.8],  MI: [41.6, -90.5,  48.3,  -82.1],
    MN: [43.4,  -97.3,  49.4,  -89.4],  MS: [30.1, -91.7,  35.1,  -88.0],
    MO: [35.9,  -95.9,  40.7,  -89.0],  MT: [44.3, -116.1, 49.1, -104.0],
    NE: [39.9,  -104.1, 43.1,  -95.3],  NV: [34.9, -120.1, 42.1, -113.9],
    NH: [42.6,  -72.6,  45.4,  -70.6],  NJ: [38.8, -75.6,  41.4,  -73.8],
    NM: [31.2,  -109.2, 37.1,  -102.9], NY: [40.4, -79.9,  45.1,  -71.7],
    NC: [33.7,  -84.4,  36.7,  -75.3],  ND: [45.9, -104.1, 49.1,  -96.5],
    OH: [38.3,  -85.0,  42.1,  -80.4],  OK: [33.5, -103.1, 37.1,  -94.3],
    OR: [41.9,  -124.7, 46.3,  -116.3], PA: [39.6, -80.6,  42.6,  -74.6],
    RI: [41.1,  -71.9,  42.1,  -71.0],  SC: [32.0, -83.5,  35.3,  -78.4],
    SD: [42.4,  -104.1, 46.1,  -96.3],  TN: [34.9, -90.4,  36.7,  -81.6],
    TX: [25.7,  -106.7, 36.6,  -93.4],  UT: [36.9, -114.1, 42.1, -108.9],
    VT: [42.6,  -73.5,  45.1,  -71.4],  VA: [36.5, -83.7,  39.5,  -75.1],
    WA: [45.5,  -124.9, 49.1,  -116.9], WV: [37.1, -82.7,  40.7,  -77.6],
    WI: [42.4,  -93.0,  47.2,  -86.2],  WY: [40.9, -111.2, 45.1, -104.0]
};

/**
 * Checks whether a lat/lng coordinate falls within the given state's approximate
 * bounding box. Fails OPEN (returns true) if the state has no known bounding box, or if
 * lat/lng aren't valid finite numbers — this is a coarse sanity check, not a precise
 * geofence, so an unrecognized state code should never itself block a search.
 *
 * @param {number} lat
 * @param {number} lng
 * @param {string} stateCode - 2-letter uppercase state code
 * @returns {boolean}
 */
export function isCoordInStateBounds(lat, lng, stateCode) {
    const box = STATE_BOUNDING_BOXES[stateCode?.toUpperCase()];
    if (!box) return true;
    if (typeof lat !== 'number' || typeof lng !== 'number' || Number.isNaN(lat) || Number.isNaN(lng)) return true;

    const [south, west, north, east] = box;
    return lat >= south && lat <= north && lng >= west && lng <= east;
}

